import { useState, memo, useCallback, useEffect } from "react";
import { ClickAwayListener } from "@mui/base";
import { Search } from "react-feather";

import { MenuProps } from "./types";
import { useTheme, Box, Checkbox, Popper, TextField, InputAdornment, BoxProps } from "../Mui";
import { NoData } from "../NoData";

type MenuFilter = (menu: MenuProps, search: string | undefined) => boolean;

interface MenuItemNodeWrapperUIProps {
  menu: MenuProps;
  onClick: (menu: MenuProps) => void;
  children: React.ReactNode;
  sx?: BoxProps["sx"];
}

export function MenuItemNodeWrapperUI({ menu, sx, onClick, children }: MenuItemNodeWrapperUIProps) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        height: "48px",
        padding: "0 16px",
        cursor: "pointer",
        "&:hover": {
          background: "#313D67",
          color: "text.primary",
        },
        ...sx,
      }}
      onClick={() => onClick(menu)}
    >
      {children}
    </Box>
  );
}

interface MenuItemNodeProps {
  menu: MenuProps;
  isFiltered: boolean;
  customLabel?: boolean;
  multiple?: boolean;
  value?: any;
  onClick: (menu: MenuProps) => void;
  onCheckboxChange: (checked: boolean, selectedValue: any) => void;
  setFilteredTokens: React.Dispatch<React.SetStateAction<{ [tokenId: string]: boolean }>>;
}

export const MenuItemNode = memo(
  ({
    customLabel,
    menu,
    isFiltered,
    multiple,
    value,
    onClick,
    onCheckboxChange,
    setFilteredTokens,
  }: MenuItemNodeProps) => {
    useEffect(() => {
      setFilteredTokens((prev) => ({ ...prev, [menu.value]: isFiltered }));
    }, [isFiltered, menu]);

    return customLabel ? (
      <Box onClick={() => onClick(menu)} sx={{ ...(isFiltered ? { display: "none" } : {}) }}>
        {menu.label}
      </Box>
    ) : (
      <MenuItemNodeWrapperUI
        onClick={() => onClick(menu)}
        menu={menu}
        sx={{
          display: isFiltered ? "none" : "flex",
        }}
      >
        {multiple && (
          <Box sx={{ margin: "0 5px 0 0" }}>
            <Checkbox
              sx={{ padding: 0 }}
              onChange={(event: React.ChangeEvent<HTMLInputElement>, checked: boolean) =>
                onCheckboxChange(checked, menu.value)
              }
              checked={value?.includes(menu.value)}
            />
          </Box>
        )}

        {menu.label}
      </MenuItemNodeWrapperUI>
    );
  },
);

interface MenuNodeProps {
  menus: MenuProps[];
  customLabel?: boolean;
  multiple?: boolean;
  value?: any;
  onClick: (menu: MenuProps) => void;
  onCheckboxChange: (checked: boolean, selectedValue: any) => void;
  menuFilter?: MenuFilter;
  setFilteredTokens: React.Dispatch<React.SetStateAction<{ [tokenId: string]: boolean }>>;
  search: string | undefined;
}

const MenuNode = memo(
  ({
    customLabel,
    menus,
    multiple,
    value,
    onClick,
    onCheckboxChange,
    menuFilter,
    setFilteredTokens,
    search,
  }: MenuNodeProps) => {
    return (
      <>
        {menus.map((menu) => {
          const isFiltered = menuFilter && menuFilter(menu, search);

          return (
            <MenuItemNode
              key={menu.value}
              isFiltered={isFiltered}
              menu={menu}
              customLabel={customLabel}
              multiple={multiple}
              value={value}
              onCheckboxChange={onCheckboxChange}
              onClick={onClick}
              setFilteredTokens={setFilteredTokens}
            />
          );
        })}
      </>
    );
  },
);

export interface DropDownMenuProps {
  value?: any;
  onChange?: (value: any) => void;
  required?: boolean;
  menus: MenuProps[];
  CustomNoData?: React.ReactNode;
  multiple?: boolean;
  menuMaxHeight?: string;
  search?: boolean;
  onSearch?: (search: string | undefined) => void;
  customLabel?: boolean;
  menuFilter?: MenuFilter;
  minMenuWidth?: string;
  anchor: any | undefined;
  onClose: () => void;
  onMenuClick: () => void;
  menuWidth?: number;
}

export const __DropDownMenu = ({
  value,
  onChange,
  menus,
  CustomNoData,
  multiple = false,
  menuMaxHeight,
  onSearch,
  search: hasSearch,
  customLabel,
  menuFilter,
  minMenuWidth = "120px",
  onClose,
  menuWidth,
  onMenuClick,
  anchor,
}: DropDownMenuProps) => {
  const theme = useTheme();
  const [search, setSearch] = useState<undefined | string>(undefined);
  const [filteredTokens, setFilteredTokens] = useState<{ [tokenId: string]: boolean }>({});

  const handleClose = () => {
    setSearch(undefined);
    onClose();
    if (onSearch) onSearch(undefined);
  };

  const handleMenuItemClick = (menu: MenuProps) => {
    if (!multiple) {
      setSearch(undefined);
      onMenuClick();
      if (onSearch) onSearch(undefined);
      if (onChange) onChange(menu.value);
    } else {
      const oldSelected = value ? [...value] : [];

      if (value?.includes(menu.value)) {
        const index = oldSelected.findIndex((item) => item === menu.value);

        if (index !== -1) {
          oldSelected.splice(index, 1);
          if (onChange) onChange(oldSelected);
        }
      } else {
        const newSelected = [...oldSelected, menu.value];
        if (onChange) onChange(newSelected);
      }
    }
  };

  const handleCheckboxChange = useCallback(
    (checked: boolean, selectedValue: any) => {
      if (onChange) {
        const oldSelected = value ? [...value] : [];

        if (checked) {
          const newSelected = [...oldSelected, selectedValue];
          onChange(newSelected);
        } else {
          const index = oldSelected.findIndex((item) => item === selectedValue);

          if (index !== -1) {
            oldSelected.splice(index, 1);
            onChange(oldSelected);
          }
        }
      }
    },
    [onChange, value],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      if (onSearch) onSearch(value);
    },
    [onSearch],
  );

  return (
    <Popper
      id="drop-down-menus"
      open={Boolean(anchor)}
      anchorEl={anchor}
      style={{
        width: menuWidth ?? "fit-content",
        minWidth: minMenuWidth ?? "fit-content",
        background: theme.colors.darkLevel3,
        border: "1px solid #49588E",
        borderRadius: "12px",
        overflow: "hidden",
        zIndex: 100000,
      }}
    >
      <ClickAwayListener onClickAway={handleClose}>
        <Box>
          {hasSearch ? (
            <Box
              sx={{
                margin: "8px 0",
                padding: "0 6px",
                "& input": {
                  color: theme.palette.text.primary,
                },
              }}
            >
              <TextField
                sx={{
                  borderRadius: "8px",
                  padding: "5px 10px",
                  fontSize: "14px",
                  background: theme.palette.background.level1,
                }}
                placeholder="Search"
                variant="standard"
                onChange={({ target: { value } }) => handleSearchChange(value)}
                value={search}
                slotProps={{
                  input: {
                    disableUnderline: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search size="12px" color={theme.palette.text.secondary} />
                      </InputAdornment>
                    ),
                  },
                }}
                fullWidth
              />
            </Box>
          ) : null}

          <Box sx={{ maxHeight: menuMaxHeight ?? "540px", overflow: "hidden auto" }}>
            <MenuNode
              menus={menus}
              customLabel={customLabel}
              multiple={multiple}
              value={value}
              onClick={handleMenuItemClick}
              onCheckboxChange={handleCheckboxChange}
              menuFilter={menuFilter}
              setFilteredTokens={setFilteredTokens}
              search={search}
            />

            {menus.length === 0 || Object.values(filteredTokens).every((isFiltered) => isFiltered)
              ? CustomNoData || <NoData />
              : null}
          </Box>
        </Box>
      </ClickAwayListener>
    </Popper>
  );
};

export const DropDownMenu = memo(__DropDownMenu);
